import { useMemo } from "react";
import type { TenantVerticalLiteral } from "@eduviaos/shared";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import {
  clientFacingText,
  getVerticalProfile,
  permissionLabelFor,
  roleLabelFor,
  type VerticalProfile,
  type VerticalTerms,
} from "@/lib/verticalProfile";

export interface TermsApi {
  vertical: TenantVerticalLiteral;
  profile: VerticalProfile;
  terms: VerticalTerms;
  /** Rewrites a sentence into the vertical's vocabulary. */
  ct: (text: string) => string;
  /** Display label for a stored role (`teacher` → Mentor on a study centre). */
  roleLabel: (role: string, fallback: string) => string;
  /** Display label for a permission code. */
  permissionLabel: (code: string) => string;
}

export function termsApiFor(vertical: TenantVerticalLiteral): TermsApi {
  const profile = getVerticalProfile(vertical);
  return {
    vertical,
    profile,
    terms: profile.terms,
    ct: (text) => clientFacingText(vertical, text),
    roleLabel: (role, fallback) =>
      clientFacingText(vertical, roleLabelFor(vertical, role, fallback)),
    permissionLabel: (code) => permissionLabelFor(vertical, code),
  };
}

/**
 * Phase SC-22 — the vertical's client-facing vocabulary for the current
 * tenant. Pass `override` when a parent already knows the vertical (and
 * in tests); otherwise it reads the tenant from `useEnabledModules`.
 * Falls back to school wording while the tenant is still loading.
 */
export function useTerms(override?: TenantVerticalLiteral): TermsApi {
  const { vertical } = useEnabledModules();
  const resolved = override ?? vertical ?? "school";
  return useMemo(() => termsApiFor(resolved), [resolved]);
}
